<template>
    <app-layout title="Settings">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Settings - Promo Codes</template>

                <template #actions>
                    <jet-button type="button" class="h-7 leading-none" @click="showModal = 'add'" >
                        Add Promo Code
                    </jet-button>
                </template>
            </page-header>

            <settings-navigation />

            <div class="flex px-8">
                <div class="flex gap-x-2 w-3/4">
                    <div class="flex flex-col flex-1">
                        <jet-input type="text" class="h-7" name="search" v-model="mainForm.search" @input="handleSubmit" placeholder="Search..."/>
                    </div>
                    <div class="self-end">
                        <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset" >
                            Reset
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>

            <div class="px-8 mt-2">
                <div class="rounded-t overflow-hidden border border-b-0 w-3/4">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="code">Code</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="order_type">Order type</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="type">Type</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="discount_amount">Discount</sortable-header>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="code in codes.data" :key="code.id"
                                :class="{
                                    'cursor-pointer': true,
                                    'text-gray-400': code.customer_id !== null,
                                }"
                                @click="editPromoCode(code)"
                            >
                                <td class="text-left">
                                    {{ code.code }}
                                </td>
                                <td class="text-left">
                                    Retail {{ code.order_type === 'retail_produce' ? 'Produce' : 'Box' }}
                                </td>
                                <td class="text-left">
                                    {{ code.type === 'prc_discount' ? '%' : '$' }} Discount
                                </td>
                                <td class="text-left">
                                    {{ code.type === 'dollar_discount' ? '$ ' : '&nbsp;&nbsp;' }}{{ Number(code.discount_amount) }}{{ code.type === 'prc_discount' ? ' %' : '' }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4 w-3/4">
                    <pagination class="mt-0 mb-0" :links="codes.links" />

                    <div class="w-40">
                        <multiselect
                            v-model.number="mainForm.paginate"
                            :options="paginateOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="top"
                            @change="handleSubmit"
                        />
                    </div>
                </div>
            </div>

            <div class="px-8 mt-2">
                Total Unredeemed Gift Card Amounts: $ {{ unredeemed_gift_card_amount }}
            </div>

            <!-- Add/Edit Promo Code -->
            <jet-dialog-modal :show="showModal" @close="closeModal">
                <template #title>
                     <h3 class="text-2xl">{{ showModal == 'add' ? 'Add' : 'Edit' }} Promo Code</h3>
                </template>

                <template #content>
                    <form @submit.prevent="submitForm" class="my-6">
                        <div class="flex gap-x-4 items-center mb-4">
                            <jet-label value="Code:" class="mb-0" style="min-width: 90px;" />
                            <jet-input v-model="form.code" type="text" class="w-60"/>
                            <jet-input-error :message="form.errors.code" />
                        </div>

                        <div class="flex gap-x-4 items-center mb-4">
                            <jet-label value="Discount:" class="mb-0" style="min-width: 90px;" />
                            <div class="flex flex-col items-start w-32">
                                <div class="flex flex-row items-center mb-2">
                                    <jet-input v-model="form.type" type="radio" value="prc_discount" name="type" id="prc_discount" :checked="form.type === 'prc_discount'"/>
                                    <jet-label value="% Discount" class="mb-0 ml-2" style="width: 90px;" for="prc_discount"/>
                                </div>
                                <div class="flex flex-row items-center">
                                    <jet-input v-model="form.type" type="radio" value="dollar_discount" name="type" id="dollar_discount" :checked="form.type === 'dollar_discount'"/>
                                    <jet-label value="$ Discount" class="mb-0 ml-2" style="width: 90px;" for="dollar_discount"/>
                                </div>
                            </div>
                            <div class="flex flex-row items-center">
                                <span v-if="form.type === 'dollar_discount'" style="height: 34px;"
                                    class="pt-1 px-3 border border-r-0 rounded-r-none rounded-l"
                                >$</span>
                                <jet-input type="number" class="w-20" v-model="form.discount_amount"
                                    :class="{
                                        'rounded-l-none': form.type === 'dollar_discount',
                                        'rounded-r-none': form.type === 'prc_discount',
                                    }"
                                />
                                <span v-if="form.type === 'prc_discount'" style="height: 34px;"
                                    class="pt-1 px-3 border border-l-0 rounded-l-none rounded-r"
                                >%</span>
                            </div>
                            <div class="flex flex-col items-left">
                                <jet-input-error :message="form.errors.type" />
                                <jet-input-error :message="form.errors.discount_amount" />
                            </div>
                        </div>

                        <div class="flex gap-x-4 items-start">
                            <jet-label value="Usage:" class="mb-0" style="min-width: 90px;" />
                            <div class="flex flex-col items-start w-60">
                                <div class="flex flex-row items-center mb-2">
                                    <jet-input v-model="form.usage" type="radio" value="1" name="usage" id="usage_once" :checked="form.usage === 1"/>
                                    <jet-label value="One Time" class="mb-0 ml-2" for="usage_once"/>
                                </div>
                                <div class="flex flex-row items-center mb-2">
                                    <jet-input v-model="form.usage" type="radio" value="0" name="usage" id="usage_unlimited" :checked="form.usage === 0"/>
                                    <jet-label value="Unlimited" class="mb-0 ml-2" for="usage_unlimited"/>
                                </div>
                                <div class="flex flex-row items-center mb-2">
                                    <jet-input v-model="form.usage" type="radio" value="-1" name="usage" id="usage_once_per_customer" :checked="form.usage === -1"/>
                                    <jet-label value="Once Per Customer" class="mb-0 ml-2" for="usage_once_per_customer"/>
                                </div>
                                <div class="flex flex-row items-center">
                                    <jet-input v-model="form.usage" type="radio" value="2" name="usage" id="usage_once_only_new_customer" :checked="form.usage === 2"/>
                                    <jet-label value="New customers only" class="mb-0 ml-2" for="usage_once_only_new_customer"/>
                                </div>
                            </div>
                            <jet-input-error :message="form.errors.usage" />
                        </div>

                        <div class="flex gap-x-4 items-center mt-4">
                            <jet-label value="Employee code:" class="mb-0" style="min-width: 90px;" />
                            <toggle v-model="form.is_employee_code"/>
                            <jet-input-error :message="form.errors.is_employee_code" />
                        </div>

                        <div v-if="showModal == 'edit'" class="flex gap-x-4 items-start mt-4" >
                            <jet-label value="Order history:" class="mb-0" style="min-width: 90px;" />
                            <div v-if="form.loading_outlet_orders">
                                <img src="/images/icons/loading-pulse.svg" height="64" width="64" class="-mt-3" />
                            </div>
                            <div v-else-if="!form.outlet_orders.length" class="text-muted">
                                No Related Orders
                            </div>
                            <div v-else class="flex flex-col">
                                <div v-for="order, index in form.outlet_orders" :key="order.id">
                                    {{ `${index + 1}. ` }}
                                    <Link :href="orderUrl(order.id)" preserve-scroll class="underline text-indigo-700" @click.stop>
                                        {{ `${order.order_number} ($${Number(order.promo_code_amount).toFixed(2)})` }}
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <input type="submit" class="hidden" ref="submitFormButton" />
                    </form>
                </template>

                <template #footer>
                    <jet-danger-button v-if="showModal === 'edit'" class="mr-2" @click="deletePromoCode(form)">
                        Delete
                    </jet-danger-button>

                    <jet-button class="mr-2" @click="$refs.submitFormButton.click()" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button @click="closeModal">
                        Cancel
                    </jet-tertiary-button>
                </template>
            </jet-dialog-modal>

            <!-- Delete Promo Code -->
            <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal = false">
                <template #title>
                    Delete Promo Code
                </template>

                <template #content>
                    Are you sure you would like to delete <strong>{{ form.code }}</strong>?
                </template>

                <template #footer>
                    <jet-danger-button @click="handleDeletePromoCode">
                        Yes
                    </jet-danger-button>

                    <jet-tertiary-button class="ml-2" @click="showDeleteModal = false">
                        No
                    </jet-tertiary-button>
                </template>
            </jet-confirmation-modal>

        </form>
    </app-layout>
</template>

<script>
import { Link, useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Pagination from '@/Jetstream/Pagination'
import SortableHeader from '@/Shared/SortableHeader';
import Toggle from '@vueform/toggle';

import '@vueform/toggle/themes/default.css';
import '@vueform/multiselect/themes/default.css';

export default {
    setup(props) {
        const mainForm = useForm({
            paginate: props.paginate,
            search: props.search,
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
        });

        const form = useForm({
            id: null,
            code: '',
            type: '',
            discount_amount: '',
            usage: '',
            is_employee_code: false,
            loading_outlet_orders: false,
            outlet_orders: [],
        });

        return { mainForm, form }
    },

    components: {
        Link,
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetDialogModal,
        JetConfirmationModal,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        SettingsNavigation,
        Multiselect,
        Pagination,
        SortableHeader,
        Toggle
    },

    props: ['codes', 'search', 'sortColumn', 'sortDirection', 'paginate', 'unredeemed_gift_card_amount'],

    mounted() {
        const params = new URLSearchParams(window.location.search);
        const id = parseInt(params.get('id'));
        let code = this.codes.data.find(code => code.id === id);

        if (code) {
            this.editPromoCode(code);
        }
    },

    data() {
        return {
            showModal: false,
            showDeleteModal: false,

            outletOrdersCancel: null,

            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.mainForm.get(route('settings.promo_codes.index'), { preserveState: true });
        }, 250),

        handleReset () {
            this.mainForm.search = '';
            this.mainForm.sortColumn = 'id';
            this.mainForm.sortDirection = 'asc';
            this.handleSubmit();
        },

        closeModal() {
            this.form.reset();
            this.form.clearErrors();
            this.showModal = false;
        },

        editPromoCode(data) {
            if (this.outletOrdersCancel) this.outletOrdersCancel.cancel();
            this.outletOrdersCancel = axios.CancelToken.source();

            this.form.id = data.id;
            this.form.code = data.code;
            this.form.type = data.type;
            this.form.usage = data.usage;
            this.form.discount_amount = data.discount_amount;
            this.form.is_employee_code = data.is_employee_code;

            this.form.loading_outlet_orders = true;
            axios.get(route('settings.promo_codes.getOrders', {id: this.form.id}), { cancelToken: this.outletOrdersCancel.token })
            .then(response => {
                this.form.outlet_orders = response.data;
                this.form.loading_outlet_orders = false;
            })
            .catch(error => {
                console.error(error);
            });

            this.showModal = 'edit';
        },

        submitForm() {
            if (this.showModal === 'add') {
                this.form.transform((data) => ({
                    code: data.code,
                    type: data.type,
                    usage: data.usage,
                    discount_amount: data.discount_amount,
                    is_employee_code: data.is_employee_code
                })).post(this.route('settings.promo_codes.store'), {
                    onSuccess: () => {
                        this.form.reset();
                        this.showModal = false;
                    }
                });
            } else {
                this.form.transform((data) => ({
                    id: data.id,
                    code: data.code,
                    type: data.type,
                    usage: data.usage,
                    discount_amount: data.discount_amount,
                    is_employee_code: data.is_employee_code
                })).put(this.route('settings.promo_codes.update', this.form.id), {
                    onSuccess: () => this.closeModal()
                });
            }
        },

        deletePromoCode(data) {
            this.form.id = data.id;
            this.form.code = data.code;
            this.showDeleteModal = true;
        },

        handleDeletePromoCode() {
            this.form.delete(this.route('settings.promo_codes.destroy', this.form.id), {
                onSuccess: () => {
                    this.form.reset();
                    this.showModal = false;
                    this.showDeleteModal = false;
                }
            });
        },

        handleSort(sort) {
            this.mainForm.sortColumn = sort.column;
            this.mainForm.sortDirection = sort.direction;
            this.handleSubmit();
        },

        orderUrl(id) {
            return route('orders.show', {
                order: id,
                return: route('settings.promo_codes.index', {
                    id: this.form.id,
                    paginate: this.mainForm.paginate,
                    search: this.mainForm.search,
                    sortColumn: this.mainForm.sortColumn,
                    sortDirection: this.mainForm.sortDirection,
                }),
            });
        },
    },
}
</script>

<style scoped>
</style>
